import {
  Component,
  computed,
  inject,
  input,
  output,
  effect,
  OnDestroy,
} from '@angular/core';
import { SvgIconComponent } from 'src/app/shared/components/svg-icon/svg-icon.component';
import { MatButtonModule } from '@angular/material/button';
import { CryptoToken } from 'src/app/shared/entities/crypto-token';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { CryptotokenKeyDataService } from 'src/app/shared/store/features/cryptotoken-keys/cryptotoken-keys.service';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { CryptotokenKey } from 'src/app/shared/entities/cryptotoken-key';
import { TableComponent } from 'src/app/shared/table/components/table/table.component';
import { ColDef } from 'ag-grid-enterprise';
import { startWith } from 'rxjs';
import { IconMenuConfig } from 'src/app/shared/table/components/cell-renderers/icon-menu-renderer/icon-menu-renderer.types';
import { IconMenuRendererComponent } from 'src/app/shared/table/components/cell-renderers/icon-menu-renderer/icon-menu-renderer.component';
import { DialogService } from 'src/app/shared/dialog/services/dialog/dialog.service';
import { CreateCryptoTokenKeyComponent } from '../create-crypto-token-key/create-crypto-token-key.component';
import { Store } from '@ngrx/store';
import { UserSelector } from 'src/app/shared/store/user/user.feature';
import { MatDialog } from '@angular/material/dialog';
import { CopyCryptoTokenKeyDialogComponent } from '../copy-crypto-token-key-dialog/copy-crypto-token-key-dialog';
import { TextWithCopyRendererComponent } from 'src/app/shared/table/components/cell-renderers/text-with-copy-renderer/text-with-copy-renderer';

@Component({
  selector: 'app-crypto-token-details',
  imports: [
    SvgIconComponent,
    MatButtonModule,
    SpinnerComponent,
    TableComponent,
    CreateCryptoTokenKeyComponent,
  ],
  templateUrl: './crypto-token-details.component.html',
  styleUrl: './crypto-token-details.component.scss',
})
export class CryptoTokenDetailsComponent implements OnDestroy {
  private langService = inject(LangService);
  private dialogService = inject(DialogService);
  private dialog = inject(MatDialog);
  private cryptotokenKeyDataService = inject(CryptotokenKeyDataService);
  private store = inject(Store);
  isEditor = toSignal(this.store.select(UserSelector.selectIsEditor));
  labels = this.langService.lang.features.cryptotokenlist.details;
  value = input<CryptoToken>();
  rowData = toSignal(this.cryptotokenKeyDataService.data$.pipe(startWith([])));

  back = output();

  isLoadingKeys = toSignal(this.cryptotokenKeyDataService.isLoading$);
  isDeletingKey = toSignal(this.cryptotokenKeyDataService.isDeleting$);

  private updateKeysEffect = () => {
    const token = this.value();
    const tokenName = token?.tokenName;
    if (tokenName) {
      this.cryptotokenKeyDataService.ensure(tokenName);
    }
  };

  title = computed(() => this.labels.title(this.value()?.tokenName ?? ''));

  onBack() {
    this.back.emit();
  }

  constructor() {
    effect(this.updateKeysEffect);
  }

  ngOnDestroy() {
    this.cryptotokenKeyDataService.clear();
  }

  onDeleteKey(ctKey: CryptotokenKey) {
    this.dialogService.withConfirmation({
      text: this.labels.actions.delete.confirmation(ctKey.details.alias),
    })(() => {
      this.cryptotokenKeyDataService.delete(ctKey);
    });
  }

  onCopyKey(ctKey: CryptotokenKey) {
    this.dialog.open(CopyCryptoTokenKeyDialogComponent, {
      data: { value: ctKey },
    });
  }

  private actionButtons: IconMenuConfig<CryptotokenKey> = {
    buttons: [
      {
        icon: 'file_copy',
        tooltip: this.labels.actions.copy.tooltip,
        callback: data => this.onCopyKey(data),
      },
      {
        icon: 'delete',
        tooltip: this.labels.actions.delete.tooltip,
        callback: data => this.onDeleteKey(data),
      },
    ],
  };

  private dataColumnDefs: ColDef<CryptotokenKey>[] = [
    {
      headerName: this.labels.cryptoTokenKeys.keydetails.alias,
      valueGetter: params => params.data?.details.alias,
      cellRenderer: TextWithCopyRendererComponent,
      sortable: false,
    },
    {
      headerName: this.labels.cryptoTokenKeys.keydetails.keyAlgorithm,
      valueGetter: params => params.data?.details.keyAlgorithm,
      sortable: false,
      cellRenderer: TextWithCopyRendererComponent,
      maxWidth: 120,
    },
    {
      headerName: this.labels.cryptoTokenKeys.keydetails.keySpecification,
      valueGetter: params => params.data?.details.keySpecification,
      sortable: false,
      cellRenderer: TextWithCopyRendererComponent,
      maxWidth: 160,
    },
    {
      headerName: this.labels.cryptoTokenKeys.keydetails.subjectKeyID,
      valueGetter: params => params.data?.details.subjectKeyID,
      cellRenderer: TextWithCopyRendererComponent,
      sortable: false,
    },
  ];

  private actionColumnDef = {
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
    cellRenderer: IconMenuRendererComponent,
    valueGetter: () => this.actionButtons,
  };

  private editorColumnDefs = [...this.dataColumnDefs, this.actionColumnDef];

  columnDefs = computed(() => {
    const isEditor = this.isEditor();
    return isEditor ? this.editorColumnDefs : this.dataColumnDefs;
  });
}
